// Streamlined superAdmin flow for launching a new chapter. One form, one
// click: creates the /chapters doc (which alone lights up every chapter
// dropdown, the dynamic /<slug> page, email + Slack routing, portal scoping,
// and roster snapshots), provisions the standard Stripe products/payment
// links via the provisionChapterStripe callable (stored on the doc as
// stripeLinks), and invites the founding chapter director. Ends with the
// launch checklist covering the few steps that still live outside Firestore
// (Slack bot invite, email alias, zip routing code, optional static page).
//
// The classic "+ Add Chapter" form remains for field-by-field control; this
// wizard is the fast path. Doc shape must stay in sync with emptyChapterForm /
// handleSaveChapter in LimitedPartnerPortal.jsx.
import React, { useMemo, useState } from "react";
import { doc, setDoc, Timestamp } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../../firebaseConfig.js";
import { slugifyChapter } from "../../../helpers/slugifyChapter";

const provisionChapterStripeCallable = httpsCallable(functions, "provisionChapterStripe");
const inviteUserCallable = httpsCallable(functions, "inviteUser");

const aliasForName = (value) => {
  const bare = (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  return bare ? `${bare}@goodneighbor.fund` : "";
};

const todayISO = () => new Date().toISOString().split("T")[0];

const normalizeCounties = (value) => {
  const list = String(value || "").split(/[\n,]/);
  return [...new Set(list.map((s) => s.trim()).filter(Boolean))];
};

// Founding LP badge window — how long after the founded date a new LP
// still counts as a founding member. Mirrors the options in the Chapters tab
// edit form (LimitedPartnerPortal).
const FOUNDING_WINDOW_OPTIONS = [
  { days: 90, label: "First quarter (90 days)" },
  { days: 180, label: "First two quarters (180 days)" },
  { days: 270, label: "First three quarters (270 days)" },
  { days: 365, label: "First year (365 days)" },
];

// Standard launch copy, derived from the chapter name. Directors refine it
// later from the Chapters tab — these match the voice of the existing chapters.
const buildChapterDoc = (form, order, uid) => {
  const name = form.name.trim();
  const slug = slugifyChapter(name);
  return {
    slug,
    pageSlug: slug,
    name,
    tagline: form.tagline.trim() || `Bringing belief capital to bold founders across ${name}.`,
    foundedYear: Number(form.foundedDate.slice(0, 4)),
    foundedDate: Timestamp.fromDate(new Date(form.foundedDate)),
    foundingWindowDays: FOUNDING_WINDOW_OPTIONS.some((o) => o.days === Number(form.foundingWindowDays))
      ? Number(form.foundingWindowDays)
      : 365,
    emailAlias: form.emailAlias.trim(),
    slackChannel: form.slackChannel.trim(),
    lpSlackChannel: form.lpSlackChannel.trim() || form.slackChannel.trim(),
    active: true,
    order,
    heroTitle: `$1,000 Micro-Grants for ${name} Business Ideas`,
    heroTagline: "No pitch deck required. No equity taken. Just belief in your vision and potential.",
    servingTitle: `Serving ${name}`,
    servingText: `Good Neighbor Fund ${name} supports entrepreneurs across the region. Wherever you're building, we believe in your potential.`,
    counties: normalizeCounties(form.counties),
    poweredByText: `Good Neighbor Fund is a collective giving organization. Our funding comes from Limited Partners (LPs) — local founders, operators, and community members who pool their own capital each quarter to fund the boldest new ideas in ${name}. No overhead. No bureaucracy. Just neighbors investing in neighbors.`,
    showLPs: false,
    showGallery: false,
    showImpact: true,
    showAwardees: false,
    defaultGrantAmount: null,
    createdAt: Timestamp.now(),
    createdBy: uid,
    updatedAt: Timestamp.now(),
  };
};

const STEP_LABELS = { pending: "Waiting", running: "Working…", done: "Done", error: "Failed", skipped: "Skipped" };

export default function NewChapterWizard({ user, chapters, onClose, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    tagline: "",
    foundedDate: todayISO(),
    foundingWindowDays: 365,
    emailAlias: "",
    slackChannel: "",
    lpSlackChannel: "",
    counties: "",
    directorName: "",
    directorEmail: "",
  });
  const [emailTouched, setEmailTouched] = useState(false);
  const [phase, setPhase] = useState("form"); // form | launching | done
  const [steps, setSteps] = useState({ doc: "pending", stripe: "pending", director: "pending" });
  const [stepErrors, setStepErrors] = useState({});
  const [stripeLinks, setStripeLinks] = useState(null);

  const slug = useMemo(() => slugifyChapter(form.name), [form.name]);
  const derivedAlias = useMemo(() => aliasForName(form.name), [form.name]);
  const emailAlias = emailTouched ? form.emailAlias : derivedAlias;
  const nextOrder = useMemo(
    () => (chapters || []).reduce((max, c) => Math.max(max, Number(c.order) || 0), 0) + 1,
    [chapters]
  );
  const slugTaken = !!slug && (chapters || []).some((c) => c.id === slug);
  const nameTaken = !!form.name.trim() &&
    (chapters || []).some((c) => (c.name || "").toLowerCase() === form.name.trim().toLowerCase());

  const canLaunch =
    form.name.trim() && slug && !slugTaken && !nameTaken &&
    form.foundedDate && emailAlias && form.slackChannel.trim() &&
    form.directorName.trim() && form.directorEmail.trim();

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  const markStep = (key, status, error) => {
    setSteps((s) => ({ ...s, [key]: status }));
    if (error) setStepErrors((s) => ({ ...s, [key]: error }));
  };

  const launch = async () => {
    if (!canLaunch || phase !== "form") return;
    setPhase("launching");

    // 1. Chapter doc — the linchpin; abort the run if this write fails.
    markStep("doc", "running");
    try {
      const payload = buildChapterDoc({ ...form, emailAlias }, nextOrder, user?.uid || "wizard");
      await setDoc(doc(db, "chapters", slug), payload);
      markStep("doc", "done");
    } catch (err) {
      console.error("NewChapterWizard: chapter doc create failed:", err);
      markStep("doc", "error", err.message);
      markStep("stripe", "skipped");
      markStep("director", "skipped");
      setPhase("done");
      return;
    }

    // 2. Stripe provisioning — best-effort; the checklist covers the retry.
    markStep("stripe", "running");
    try {
      const result = await provisionChapterStripeCallable({ chapterSlug: slug });
      setStripeLinks(result.data?.stripeLinks || null);
      markStep("stripe", "done");
    } catch (err) {
      console.error("NewChapterWizard: Stripe provisioning failed:", err);
      markStep("stripe", "error", err.message);
    }

    // 3. Founding director invite — best-effort; retry from Create User tab.
    markStep("director", "running");
    try {
      await inviteUserCallable({
        email: form.directorEmail.toLowerCase().trim(),
        name: form.directorName.trim(),
        role: "chapter_director",
        chapter: form.name.trim(),
        anniversary: form.foundedDate,
        chapterRole: "",
      });
      markStep("director", "done");
    } catch (err) {
      console.error("NewChapterWizard: director invite failed:", err);
      markStep("director", "error", err.message);
    }

    setPhase("done");
    if (onCreated) onCreated();
  };

  const fieldLabel = { display: "block", marginBottom: 12 };

  return (
    <section className="mb-card" style={{ padding: 20, marginBottom: 20 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <div>
          <div className="mb-eyebrow">Chapter launch</div>
          <h3 style={{ margin: "4px 0 2px" }}>New Chapter Wizard</h3>
          <p className="mb-micro" style={{ margin: 0, color: "var(--mb-ink-60)" }}>
            Creates the chapter record, provisions Stripe membership + donation links, and invites the founding director.
          </p>
        </div>
        <button type="button" className="mb-btn mb-btn-ghost" onClick={onClose} style={{ fontSize: 12 }}>
          {phase === "done" ? "Close" : "Cancel"}
        </button>
      </div>

      {phase === "form" && (
        <div className="mb-form-shell" style={{ marginTop: 16 }}>
          <div className="mb-grid mb-grid-2" style={{ gap: 16 }}>
            <div>
              <label style={fieldLabel}>
                Chapter name *
                <input type="text" value={form.name} onChange={set("name")} placeholder="Hudson Valley" />
              </label>
              {(slugTaken || nameTaken) && (
                <p className="mb-micro" style={{ color: "var(--mb-magenta)", marginTop: -6 }}>
                  A chapter with this {slugTaken ? `slug (${slug})` : "name"} already exists.
                </p>
              )}
              <label style={fieldLabel}>
                Founded date *
                <input type="date" value={form.foundedDate} onChange={set("foundedDate")} />
              </label>
              <label style={fieldLabel}>
                Founding LP window (LPs joining within this span earn the badge)
                <select value={form.foundingWindowDays} onChange={set("foundingWindowDays")}>
                  {FOUNDING_WINDOW_OPTIONS.map((o) => (
                    <option key={o.days} value={o.days}>{o.label}</option>
                  ))}
                </select>
              </label>
              <label style={fieldLabel}>
                Chapter email alias *
                <input
                  type="email"
                  value={emailAlias}
                  onChange={(e) => { setEmailTouched(true); setForm((f) => ({ ...f, emailAlias: e.target.value })); }}
                />
              </label>
              <label style={fieldLabel}>
                Tagline (shown on chapter cards — a sensible default is generated)
                <input type="text" value={form.tagline} onChange={set("tagline")} placeholder={`Bringing belief capital to bold founders across ${form.name.trim() || "the region"}.`} />
              </label>
              <label style={fieldLabel}>
                Counties served (comma or newline separated)
                <textarea rows={3} value={form.counties} onChange={set("counties")} placeholder={"Westchester County\nRockland County"} />
              </label>
            </div>
            <div>
              <label style={fieldLabel}>
                Slack channel ID — pitch review *
                <input type="text" value={form.slackChannel} onChange={set("slackChannel")} placeholder="C0XXXXXXXXX" />
              </label>
              <label style={fieldLabel}>
                Slack channel ID — LP applications (defaults to the pitch channel)
                <input type="text" value={form.lpSlackChannel} onChange={set("lpSlackChannel")} placeholder="C0XXXXXXXXX" />
              </label>
              <label style={fieldLabel}>
                Founding director name *
                <input type="text" value={form.directorName} onChange={set("directorName")} />
              </label>
              <label style={fieldLabel}>
                Founding director email *
                <input type="email" value={form.directorEmail} onChange={set("directorEmail")} />
              </label>
            </div>
          </div>

          <div style={{ background: "var(--mb-paper-deep)", border: "1px solid var(--mb-ink)", padding: "10px 14px", marginTop: 4 }}>
            <div className="mb-eyebrow" style={{ marginBottom: 4 }}>Derived automatically</div>
            <p className="mb-micro" style={{ margin: 0 }}>
              Slug / doc ID: <strong className="mb-numeral">{slug || "—"}</strong>
              {" · "}Public page: <strong className="mb-numeral">goodneighbor.fund/{slug || "—"}</strong>
              {" · "}Display order: <strong className="mb-numeral">{nextOrder}</strong>
            </p>
            <p className="mb-micro" style={{ margin: "4px 0 0", color: "var(--mb-ink-60)" }}>
              Hero, serving, and powered-by copy start from the standard templates; the director can edit them in the Chapters tab.
            </p>
          </div>

          <div style={{ marginTop: 16 }}>
            <button type="button" className="mb-btn mb-btn-ink" disabled={!canLaunch} onClick={launch}>
              Launch chapter<span className="mb-btn-arrow">→</span>
            </button>
          </div>
        </div>
      )}

      {phase !== "form" && (
        <div style={{ marginTop: 16 }}>
          {[
            { key: "doc", label: `Create /chapters/${slug} record` },
            { key: "stripe", label: "Provision Stripe products + payment links" },
            { key: "director", label: `Invite ${form.directorName.trim() || "director"} as chapter director` },
          ].map(({ key, label }) => (
            <div key={key} style={{ display: "flex", gap: 10, alignItems: "baseline", padding: "6px 0", borderBottom: "1px dashed var(--mb-ink-15)" }}>
              <span className="mb-tag" style={{ minWidth: 84, textAlign: "center", background: steps[key] === "done" ? "var(--mb-aqua)" : steps[key] === "error" ? "var(--mb-magenta)" : undefined }}>
                {STEP_LABELS[steps[key]]}
              </span>
              <div>
                <span className="mb-body" style={{ fontSize: 14 }}>{label}</span>
                {stepErrors[key] && (
                  <div className="mb-micro" style={{ color: "var(--mb-magenta)" }}>{stepErrors[key]}</div>
                )}
              </div>
            </div>
          ))}

          {phase === "done" && (
            <div style={{ marginTop: 16 }}>
              <div className="mb-eyebrow" style={{ marginBottom: 6 }}>Launch checklist — outside the portal</div>
              <ol className="mb-body" style={{ fontSize: 14, paddingLeft: 20, margin: 0, display: "grid", gap: 6 }}>
                <li>Invite the GNF Slack bot to {form.slackChannel.trim() || "the pitch channel"}{form.lpSlackChannel.trim() && form.lpSlackChannel.trim() !== form.slackChannel.trim() ? ` and ${form.lpSlackChannel.trim()}` : ""} — notifications fail silently until it joins.</li>
                <li>Create the {emailAlias || "chapter"} mailbox/alias in the email provider — confirmations cc it.</li>
                {steps.stripe !== "done" && (
                  <li>Stripe provisioning did not finish — re-run it from a terminal: <code style={{ overflowWrap: "anywhere" }}>node functions/scripts/provisionChapterStripe.js --name "{form.name.trim()}" --slug {slug}</code>.</li>
                )}
                <li>Engineering: add the chapter's zip prefixes/overrides to src/data/chapterConfig.js and functions/zipToChapter.js, then redeploy functions (zip-based routing and maps need it; everything else already works).</li>
                <li>Upload a hero photo in the Chapters tab → Photos (the public page uses the shared pennant photo until then).</li>
                <li>Optional: hand-build public/{slug}.html for chapter-specific SEO — goodneighbor.fund/{slug} already renders via the dynamic chapter page. Add it to sitemap.xml, robots.txt, and llms.txt when you do.</li>
                <li>Optional: seed the chapter's founder-resource directory (Resources tab, or a seed script like functions/scripts/seedResourcesHudsonValley.js) so the resource directory has local results.</li>
              </ol>
              {stripeLinks && (
                <div style={{ background: "var(--mb-paper-deep)", border: "1px solid var(--mb-ink)", padding: "10px 14px", marginTop: 12 }}>
                  <div className="mb-eyebrow" style={{ marginBottom: 4 }}>Stripe links (saved on the chapter record)</div>
                  <p className="mb-micro" style={{ margin: 0, wordBreak: "break-all" }}>
                    Annual: {stripeLinks.lpAnnualUrl}<br />
                    Semi-annual: {stripeLinks.lpSemiAnnualUrl}<br />
                    Chapter fee: {stripeLinks.chapterFeeUrl}<br />
                    Donation: {stripeLinks.donationUrl}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
